import React from "react";
import {
  Box,
  Flex,
  Button,
  // useColorMode,
  IconButton,
  Image,
  Container,
  // Tooltip,
  useDisclosure,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  VStack,
  Link,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import { /* SunIcon, MoonIcon, */ HamburgerIcon } from "@chakra-ui/icons";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import { FESTIVAL } from "./data/festival";

const NAV_ITEMS = [
  { to: "/", label: "HOME" },
  { to: "/about", label: "ABOUT" },
  { to: "/entertainment", label: "ENTERTAINMENT" },
  { to: "/map", label: "FESTIVAL MAP", also: ["/giveaway"] },
  { to: "/international-zone", label: "INTERNATIONAL ZONE" },
];

const WorkWithUsButton = ({ showLabel = false, onClick }) => {
  return (
    <Button
      as={RouterLink}
      to="/work-with-us"
      onClick={onClick}
      aria-label="Work With Us"
      bg="white"
      color="#0C7FBC"
      variant="solid"
      size="md"
      fontWeight="bold"
      px={3}
      border="2px solid white"
      _hover={{
        bg: "rgba(255,255,255,0.9)",
        transform: "translateY(-2px)",
        boxShadow: "0 4px 20px rgba(12, 127, 188, 0.4)",
      }}
    >
      <Image
        src="/icons2/work.webp"
        alt=""
        height="24px"
        width="24px"
        objectFit="contain"
        mr={showLabel ? 2 : 0}
      />
      {showLabel ? "Work With Us" : null}
    </Button>
  );
};

const Header = () => {
  // const { colorMode, toggleColorMode } = useColorMode();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const isActive = (item) =>
    item.to === "/" ? pathname === "/" : pathname === item.to || (item.also || []).includes(pathname);

  const buttonStyle = {
    variant: "ghost",
    size: "md",
    fontFamily: "'Poppins', sans-serif",
    fontWeight: "600",
    fontSize: "sm",
    letterSpacing: "wide",
    color: "white",
    whiteSpace: "nowrap",
    _hover: { 
      bg: "rgba(255,255,255,0.2)",
      color: "white",
      transform: "translateY(-1px)"
    },
  };

  const navLinkProps = (item) => ({
    as: RouterLink,
    to: item.to,
    "aria-current": isActive(item) ? "page" : undefined,
    ...buttonStyle,
    ...(isActive(item)
      ? { bg: "rgba(255,255,255,0.16)", boxShadow: "inset 0 -3px 0 #FFD52E" }
      : {}),
  });

  return (
    <>
      <Box
        as={motion.nav}
        aria-label="Primary navigation"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        bg="#063F74"
        color="white"
        boxShadow="0 6px 24px rgba(6, 63, 116, 0.28)"
        borderBottom="4px solid #F47A22"
        position="fixed"
        top={0}
        left={0}
        right={0}
        zIndex={10}
      >
        <Container maxW="container.xl">
          <Flex py={4} alignItems="center" justifyContent="space-between">
            <Box
              as="button"
              type="button"
              aria-label="Go to the Culver City Arts Festival homepage"
              onClick={() => navigate("/")}
              display="flex"
              alignItems="center"
              borderRadius="md"
              _focusVisible={{
                outline: "3px solid #FFD52E",
                outlineOffset: "4px",
              }}
            >
              <Image
                src="/brand/arts-festival-logo.webp"
                alt="Culver City Arts Festival"
                height={{ base: "44px", md: "58px" }}
                maxW={{ base: "210px", md: "310px" }}
                width="auto"
                objectFit="contain"
                loading="eager"
                decoding="async"
                filter="drop-shadow(0 4px 10px rgba(0,0,0,0.22))"
                fallback={<Box height="50px" width="200px" bg="rgba(255,255,255,0.2)" borderRadius="md" />}
              />
            </Box>
            <Flex alignItems="center">
              <Box display={{ base: "none", lg: "flex" }} alignItems="center" mr={4}>
                {NAV_ITEMS.map((item) => (
                  <Button key={item.to} {...navLinkProps(item)} mr={2}>
                    {item.label}
                  </Button>
                ))}
                <Link href={FESTIVAL.donateUrl} isExternal>
                  <Button
                    as={motion.button}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    bg="rgba(255,255,255,0.9)"
                    color="#0C7FBC"
                    variant="solid"
                    size="md"
                    mr={2}
                    fontWeight="bold"
                    _hover={{
                      bg: "white",
                      boxShadow: "0 4px 15px rgba(255,255,255,0.3)"
                    }}
                  >
                    Donate
                  </Button>
                </Link>
                <WorkWithUsButton />
              </Box>
              {/* Dark mode toggle commented out */}
              {/*
              <Tooltip label="Toggle color mode">
                <IconButton
                  icon={colorMode === "light" ? <MoonIcon /> : <SunIcon />}
                  onClick={toggleColorMode}
                  variant="ghost"
                  aria-label="Toggle color mode"
                  mr={2}
                  color="white"
                  _hover={{ bg: "rgba(255,255,255,0.2)" }}
                />
              </Tooltip>
              */}
              <IconButton
                icon={<HamburgerIcon />}
                onClick={onOpen}
                variant="ghost"
                aria-label="Open menu"
                display={{ base: "flex", lg: "none" }}
                color="white"
                _hover={{ bg: "rgba(255,255,255,0.2)" }}
              />
            </Flex>
          </Flex>
        </Container>
      </Box>

      {/* Mobile Menu Drawer */}
      <Drawer isOpen={isOpen} placement="right" onClose={onClose}>
        <DrawerOverlay />
        <DrawerContent bg="#063F74" color="white">
          <DrawerCloseButton color="white" />
          <DrawerHeader color="white" borderBottomColor="rgba(255,255,255,0.2)">Menu</DrawerHeader>
          <DrawerBody>
            <VStack spacing={4} align="stretch">
              {NAV_ITEMS.map((item) => (
                <Button key={item.to} {...navLinkProps(item)} onClick={onClose} justifyContent="flex-start">
                  {item.label}
                </Button>
              ))}
              <Link href={FESTIVAL.donateUrl} isExternal>
                <Button
                  as={motion.button}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  bg="white"
                  color="#0C7FBC"
                  variant="solid"
                  size="md"
                  width="100%"
                  fontWeight="bold"
                  _hover={{
                    bg: "rgba(255,255,255,0.9)",
                    boxShadow: "0 4px 15px rgba(255,255,255,0.3)"
                  }}
                >
                  Donate
                </Button>
              </Link>
              <WorkWithUsButton showLabel onClick={onClose} />
            </VStack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
};

export default Header;
